import React, { useState, useEffect, useRef } from "react";
import MissionsRecurrentes, { ROLES_CODES_MISSION } from "./MissionsRecurrentes";
import { demanderNotificationMissions, envoyerNotificationMissions } from "./notificationMissions";
import { Building2, Save, Users, Briefcase, User, X } from "lucide-react";
import { httpClient } from "../../HttpClient";
import { useEtatGlobal } from "../EtatGlobal.js";
import { showStatusToast } from "../../components/ui/Toast";
import { dialog } from "../../components/ui/Dialog";
import { useUpdateClient } from "./useClients.js";
import FormOverlay from "../../components/ui/FormOverlay";
import NouveauContactForm from "./kyc-lab/organisms/NouveauContactForm";
import { PLATEFORME_ELECTRONIQUE_OPTIONS } from "./plateformeElectroniqueOptions";
import ReferentielSelect from "./kyc-lab/atoms/ReferentielSelect";
import FormeSocialeSelect from "./kyc-lab/atoms/FormeSocialeSelect";
import { useDossierSecret, FenetreAccesSecret, estSecret, voitChoixSecret } from "./DossierSecret";

const EMPTY_DRAFT_ANNEE = {
  annee: "",
  expertComptable: "",
  email_expertComptable: "",
  id_expertComptable: "",
  collaborateur: "",
  email_collaborateur: "",
  id_collaborateur: "",
  collaborateur_niv2: "",
  email_collaborateur_niv2: "",
  id_collaborateur_niv2: "",
  social: "",
  email_social: "",
  id_social: "",
  juridique: "",
  email_juridique: "",
  id_juridique: "",
};

function Update({ userId, onCancel, onUserUpdated, selectedClient }) {
  const updateClientMutation = useUpdateClient();
  const [client, setClient] = useState({
    civilite: "",
    nom: "",
    nomClient: "",
    prenomClient: "",
    email: "",
    mdp: "",
    telephone: "",
    fonctionContact: "",
    civilite2: "",
    nomClient2: "",
    prenomClient2: "",
    fonctionContact2: "",
    email2: "",
    telephone2: "",
    formeSociale: [],
    dateClotureJour: "",
    dateClotureMois: "",
    dateClotureAnnee: "2026",
    dateDebutMission: "",
    regimeFiscal: "",
    regimeTVA: "",
    taxeSalaires: "",
    expertComptable: "",
    superviseur: "",
    collaborateur: "",
    collaborateur_niv2: "",
    missionComptable: "non",
    missionSociale: "non",
    missionJuridique: "non",
    missionIR: "non",
    ponderation: "",
    activite: "",
    plateformeElectronique: "",
    holding: false,
    personnePhysique: false,
    email_expertComptable: "",
    id_expertComptable: "",
    email_superviseur: "",
    id_superviseur: "",
    email_collaborateur: "",
    id_collaborateur: "",
    email_collaborateur_niv2: "",
    id_collaborateur_niv2: "",
    id_social: "",
    email_social: "",
    groupement: "",
    siren: "",
    siret: "",
    adresse: "",
    cabinetId: "",
    status: "",
    archiveDate: "",
    statusDetails: "",
  });
  const {
    utilisateur,
    users: allUsers,
    fetchSuiviBilan,
    fetchSuiviSocial,
    fetchSuiviJuridique,
    fetchJuridiquePonctuelle,
    fetchSuiviCFE,
    fetchSuiviCompta,
    patchSuiviBilanClient,
    patchSuiviBilanEquipeAnnee,
    patchSuiviJuridiqueClient,
    patchJuridiquePonctuelleClient,
    patchSuiviCFEClient,
    patchSuiviComptaClient,
    annuaireData,
    fetchAnnuaire,
    updateAnnuaireContact,
    kycReferentiel,
  } = useEtatGlobal();

  // Sécurité : seuls ces rôles peuvent voir/modifier le statut du dossier
  // (Actif / Bloqué / Archivé) — les autres rôles ne voient qu'un badge en
  // lecture seule (voir le rendu du statut plus bas). Miroir du contrôle
  // équivalent côté backend (bdClientController.js#updateClient).
  const STATUS_EDIT_ROLES = ["ADMINISTRATEUR", "EXPERT_COMPTABLE", "SECRETARIAT"];
  const canEditStatus =
    utilisateur?.allCabinetsAccess || STATUS_EDIT_ROLES.includes(utilisateur?.role);

  // Bloquer / débloquer un dossier (litige de recouvrement) : réservé aux
  // administrateurs, experts-comptables et au secrétariat, comme le bouton de
  // la fiche client (miroir de BLOCAGE_ROLES côté backend, bdClientController.js).
  const peutBloquer = ["ADMINISTRATEUR", "EXPERT_COMPTABLE", "SECRETARIAT"].includes(
    utilisateur?.role,
  );
  const dejaBloque = selectedClient?.status === "Bloqué";

  // « Secret » dans la liste du statut (demande de Jonathan, 23/10/2026) :
  // visible d'Anthony, Samuel et Jonathan seulement. Ce n'est pas un statut :
  // il passe le dossier en mode secret (après confirmation) s'il ne l'est pas
  // encore, puis ouvre la gestion des accès (voir DossierSecret.js).
  const CHOIX_SECRET = "__secret";
  const choixSecret = voitChoixSecret(utilisateur);
  const dossierSecret = useDossierSecret(selectedClient);
  const dejaSecret = estSecret(selectedClient) || !!dossierSecret.etat?.secret;
  const [showAccesSecret, setShowAccesSecret] = useState(false);
  const ouvrirSecret = async () => {
    if (!dejaSecret) {
      const ok = await dialog.confirm(
        "Le dossier ne sera plus visible dans MyALFA Worksync que par les personnes autorisées. Vous pourrez ensuite gérer ces accès.",
        { title: "Passer en mode Secret", confirmLabel: "Passer en mode Secret", variant: "warning" },
      );
      if (!ok) return;
      try {
        await dossierSecret.activer();
      } catch (e) {
        return; // erreur déjà signalée par le toast (useDossierSecret)
      }
    }
    setShowAccesSecret(true);
  };

  // Sécurité : seul ADMINISTRATEUR peut renommer un dossier — suite à
  // l'incident HOLDING SAMI / HOLDING SAES (deux dossiers distincts
  // retrouvés avec le même nom). Miroir du contrôle backend
  // (bdClientController.js#updateClient).
  const NOM_EDIT_ROLES = ["ADMINISTRATEUR"];
  const canEditNom =
    utilisateur?.allCabinetsAccess || NOM_EDIT_ROLES.includes(utilisateur?.role);
  const experts = allUsers.filter((u) => u.role === "EXPERT_COMPTABLE");
  const collaborateurs = allUsers.filter((u) =>
    ["COLLABORATEUR", "SUPERVISEUR", "EXPERT_COMPTABLE"].includes(u.role),
  );
  // Niv. 2 accepte aussi les assistants (role ASSISTANT), contrairement à
  // Niv. 1 — décision confirmée suite à un ticket (Mélissa LANGLADE,
  // assistante, invisible dans ce menu alors qu'elle doit pouvoir y être
  // choisie).
  const collaborateurs_niv2 = allUsers.filter((u) =>
    ["COLLABORATEUR", "ASSISTANT"].includes(u.role),
  );
  const socials = allUsers.filter((u) => u.role === "SOCIAL");
  const juridiques = allUsers.filter((u) => u.role === "JURIDIQUE");
  const [isFetchingClient, setIsFetchingClient] = useState(true);
  const [cabinets, setCabinets] = useState([]);

  // Groupement = les contacts de l'Annuaire liés à ce client — exactement la
  // même relation que les "dossiers liés" affichés dans Annuaire.js (pas un
  // champ séparé) : rattacher/détacher ici met aussi à jour ce que voit
  // Annuaire.js, et inversement. Recherche par nom/prénom + création
  // directe d'un nouveau contact si besoin.
  const [linkedAnnuaires, setLinkedAnnuaires] = useState([]);
  const [groupementSearch, setGroupementSearch] = useState("");
  const [showGroupementDropdown, setShowGroupementDropdown] = useState(false);
  const [showCreateGroupementContact, setShowCreateGroupementContact] =
    useState(false);
  const groupementBoxRef = useRef(null);

  // Équipe cabinet surchargée par année (voir FicheClientOverlay.js pour
  // l'affichage en lecture seule) — une ligne par année, indépendante des
  // champs "par défaut" ci-dessus.
  const [equipeAnnees, setEquipeAnnees] = useState([]);
  const [showAddAnnee, setShowAddAnnee] = useState(false);
  const [draftAnnee, setDraftAnnee] = useState(EMPTY_DRAFT_ANNEE);

  useEffect(() => {
    httpClient
      .get("https://backend-myalfa.vercel.app/api/cabinets")
      .then((res) => setCabinets(res.data))
      .catch((err) => {
        console.error("Erreur lors du chargement des cabinets :", err);
      });
  }, []);

  // Ferme le dropdown de suggestions Annuaire au clic à l'extérieur.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        groupementBoxRef.current &&
        !groupementBoxRef.current.contains(event.target)
      ) {
        setShowGroupementDropdown(false);
      }
    };
    if (showGroupementDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showGroupementDropdown]);

  const groupementSuggestions = (() => {
    const tokens = groupementSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return [];
    const linkedIds = new Set(linkedAnnuaires.map((a) => a.id));
    return annuaireData
      .filter((c) => (c.nom || c.prenoms) && !linkedIds.has(c.id))
      .filter((c) => {
        const combined = `${c.nom || ""} ${c.prenoms || ""}`.toLowerCase();
        return tokens.every((t) => combined.includes(t));
      })
      .slice(0, 8);
  })();

  // Reflète l'ajout/retrait de ce client dans la liste "dossiers liés" du
  // contact côté annuaireData (état global lu par Annuaire.js) — sans ça,
  // Annuaire.js resterait périmé tant qu'aucun fetchAnnuaire() n'a eu lieu.
  const patchContactDossiersLies = (annuaireId, nextClientsForContact) => {
    updateAnnuaireContact(annuaireId, { clients: nextClientsForContact });
  };

  // Rattache immédiatement (connect additif — n'affecte jamais les autres
  // contacts déjà liés à ce client).
  const handleAttachGroupementContact = async (contact) => {
    setGroupementSearch("");
    setShowGroupementDropdown(false);
    if (linkedAnnuaires.some((a) => a.id === contact.id)) return;

    const optimisticContact = {
      id: contact.id,
      nom: contact.nom,
      prenoms: contact.prenoms,
    };
    const nextAnnuaires = [...linkedAnnuaires, optimisticContact];
    setLinkedAnnuaires(nextAnnuaires);
    // Propage tout de suite vers le cache client (liste Portefeuille +
    // fiche) sans attendre le clic sur "Enregistrer" — ce champ a sa
    // propre sauvegarde immédiate, comme l'équipe par année.
    onUserUpdated?.({ ...client, annuaires: nextAnnuaires });

    const contactInAnnuaire = annuaireData.find((a) => a.id === contact.id);
    const previousDossiersLies = contactInAnnuaire?.clients || [];
    const alreadyListed = previousDossiersLies.some(
      (c) => c.id === Number(userId),
    );
    if (!alreadyListed) {
      patchContactDossiersLies(contact.id, [
        ...previousDossiersLies,
        { id: Number(userId), nom: client.nom, nomClient: client.nomClient },
      ]);
    }

    const toast = showStatusToast({
      status: "loading",
      titre: "Rattachement en cours...",
      message: `Ajout de ${contact.nom} ${contact.prenoms} au groupement.`,
    });

    try {
      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/clients/${userId}/annuaire/${contact.id}`,
      );
      toast.update({
        status: "success",
        titre: "Contact rattaché",
        message: "Le groupement de ce client a été mis à jour.",
      });
    } catch (error) {
      console.error("Erreur rattachement contact Annuaire :", error);
      const rolledBack = linkedAnnuaires;
      setLinkedAnnuaires(rolledBack);
      onUserUpdated?.({ ...client, annuaires: rolledBack });
      if (!alreadyListed) {
        patchContactDossiersLies(contact.id, previousDossiersLies);
      }
      toast.update({
        status: "error",
        titre: "Échec du rattachement",
        message: "Le contact n'a pas pu être rattaché.",
      });
    }
  };

  const handleDetachGroupementContact = async (contact) => {
    const confirmed = await dialog.confirm(
      `Retirer ${contact.nom} ${contact.prenoms} du groupement de ce client ?`,
    );
    if (!confirmed) return;

    const nextAnnuaires = linkedAnnuaires.filter((a) => a.id !== contact.id);
    setLinkedAnnuaires(nextAnnuaires);
    onUserUpdated?.({ ...client, annuaires: nextAnnuaires });

    const contactInAnnuaire = annuaireData.find((a) => a.id === contact.id);
    const previousDossiersLies = contactInAnnuaire?.clients || [];
    patchContactDossiersLies(
      contact.id,
      previousDossiersLies.filter((c) => c.id !== Number(userId)),
    );

    const toast = showStatusToast({
      status: "loading",
      titre: "Retrait en cours...",
      message: `Retrait de ${contact.nom} ${contact.prenoms} du groupement.`,
    });

    try {
      await httpClient.delete(
        `https://backend-myalfa.vercel.app/api/clients/${userId}/annuaire/${contact.id}`,
      );
      toast.update({
        status: "success",
        titre: "Contact retiré",
        message: "Le groupement de ce client a été mis à jour.",
      });
    } catch (error) {
      console.error("Erreur retrait contact Annuaire :", error);
      const rolledBack = [...linkedAnnuaires];
      setLinkedAnnuaires(rolledBack);
      onUserUpdated?.({ ...client, annuaires: rolledBack });
      patchContactDossiersLies(contact.id, previousDossiersLies);
      toast.update({
        status: "error",
        titre: "Échec du retrait",
        message: "Le contact n'a pas pu être retiré.",
      });
    }
  };

  const handleGroupementContactCreated = (created) => {
    setShowCreateGroupementContact(false);
    // Rafraîchit l'Annuaire global pour que le nouveau contact soit
    // immédiatement disponible dans les recherches ailleurs dans l'app.
    fetchAnnuaire();
    if (created?.id) handleAttachGroupementContact(created);
  };

  useEffect(() => {
    if (!selectedClient) return;

    setIsFetchingClient(true);

    setClient({
      ...selectedClient,
      dateClotureJour: selectedClient.dateCloture.slice(8, 10),
      dateClotureMois: selectedClient.dateCloture.slice(5, 7),
      dateClotureAnnee: selectedClient.dateCloture.slice(0, 4),
    });
    setEquipeAnnees(selectedClient.equipeAnnees || []);
    setLinkedAnnuaires(selectedClient.annuaires || []);
    setGroupementSearch("");
    setShowGroupementDropdown(false);

    setIsFetchingClient(false);
  }, [selectedClient]);

  // Sécurité renforcée : quel que soit le nouveau statut choisi (Actif,
  // Bloqué, Archivé), on redemande de ressaisir le nom exact du dossier avant
  // d'appliquer le changement — le <select> restant contrôlé par
  // client.status, une saisie incorrecte ou une annulation le laisse
  // simplement inchangé (pas de retour en arrière explicite nécessaire).
  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === client.status) return;

    const typed = await dialog.prompt(
      `Pour confirmer le changement de statut vers "${newStatus}", veuillez ressaisir le nom exact du dossier : ${client.nom}`,
      "",
      { title: "Confirmation de sécurité", variant: "danger", confirmLabel: "Valider" },
    );

    if (typed === null) return; // annulé

    if (typed !== client.nom) {
      dialog.alert("Le nom saisi ne correspond pas au nom du dossier — le changement de statut a été annulé.");
      return;
    }

    setClient((prev) => ({ ...prev, status: newStatus }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setClient((prevClient) => ({
      ...prevClient,
      [name]: name === "formeSociale" ? [value] : value,
    }));
  };

  // Le cabinet est systématiquement rattaché (accès identifiants) pour ces
  // trois PA — les autres nécessitent une action du client, donc pas de
  // rattachement présumé (demande du 03/09/2026).
  const PA_ACCES_EC_PAR_DEFAUT = ["DEXT", "MEG", "MyAlfa Link"];

  // Dédié plutôt que handleChange générique : en plus de la PA elle-même,
  // recalcule la valeur par défaut d'"Accès EC" à chaque changement de PA
  // (reste modifiable ensuite manuellement via son propre select).
  const handlePlateformeElectroniqueChange = (e) => {
    const value = e.target.value;
    setClient((prevClient) => ({
      ...prevClient,
      plateformeElectronique: value,
      accesEC: PA_ACCES_EC_PAR_DEFAUT.includes(value) ? "oui" : "non",
    }));
  };

  // Reconstruit un draft éditable (avec les libellés "name lastName" affichés
  // dans les selects) à partir d'une ligne ClientEquipeAnnee (qui ne porte
  // que les id/email).
  const findUserLabel = (list, id) => {
    const u = list.find((user) => String(user.id) === String(id));
    return u ? `${u.name} ${u.lastName}` : "";
  };

  const toDraftFromEquipeAnnee = (eq) => ({
    annee: String(eq.annee),
    expertComptable: findUserLabel(experts, eq.id_expertComptable),
    email_expertComptable: eq.email_expertComptable || "",
    id_expertComptable: eq.id_expertComptable || "",
    collaborateur: findUserLabel(collaborateurs, eq.id_collaborateur),
    email_collaborateur: eq.email_collaborateur || "",
    id_collaborateur: eq.id_collaborateur || "",
    collaborateur_niv2: findUserLabel(
      collaborateurs_niv2,
      eq.id_collaborateur_niv2,
    ),
    email_collaborateur_niv2: eq.email_collaborateur_niv2 || "",
    id_collaborateur_niv2: eq.id_collaborateur_niv2 || "",
    social: findUserLabel(socials, eq.id_social),
    email_social: eq.email_social || "",
    id_social: eq.id_social || "",
    juridique: findUserLabel(juridiques, eq.id_juridique),
    email_juridique: eq.email_juridique || "",
    id_juridique: eq.id_juridique || "",
  });

  const openAddAnnee = () => {
    setDraftAnnee(EMPTY_DRAFT_ANNEE);
    setShowAddAnnee(true);
  };

  const openEditAnnee = (eq) => {
    setDraftAnnee(toDraftFromEquipeAnnee(eq));
    setShowAddAnnee(true);
  };

  const handleSaveEquipeAnnee = async () => {
    const annee = parseInt(draftAnnee.annee, 10);
    if (!Number.isInteger(annee)) {
      dialog.alert("Veuillez saisir une année valide.");
      return;
    }
    const payload = {
      email_expertComptable: draftAnnee.email_expertComptable || null,
      id_expertComptable: draftAnnee.id_expertComptable
        ? parseInt(draftAnnee.id_expertComptable, 10)
        : null,
      email_collaborateur: draftAnnee.email_collaborateur || null,
      id_collaborateur: draftAnnee.id_collaborateur
        ? parseInt(draftAnnee.id_collaborateur, 10)
        : null,
      email_collaborateur_niv2: draftAnnee.email_collaborateur_niv2 || null,
      id_collaborateur_niv2: draftAnnee.id_collaborateur_niv2
        ? parseInt(draftAnnee.id_collaborateur_niv2, 10)
        : null,
      email_social: draftAnnee.email_social || null,
      id_social: draftAnnee.id_social
        ? parseInt(draftAnnee.id_social, 10)
        : null,
      email_juridique: draftAnnee.email_juridique || null,
      id_juridique: draftAnnee.id_juridique
        ? parseInt(draftAnnee.id_juridique, 10)
        : null,
    };

    // Snapshot pour pouvoir revenir en arrière si l'API échoue.
    const previousEntry =
      equipeAnnees.find((e) => e.annee === annee) || null;
    const optimisticEntry = { ...previousEntry, ...payload, annee };

    // Mise à jour optimiste immédiate — formulaire ET Suivi Bilan (via
    // patchSuiviBilanEquipeAnnee), sans attendre la réponse serveur.
    setEquipeAnnees((prev) => {
      const others = prev.filter((e) => e.annee !== annee);
      return [...others, optimisticEntry].sort((a, b) => a.annee - b.annee);
    });
    patchSuiviBilanEquipeAnnee(userId, annee, optimisticEntry);
    setShowAddAnnee(false);

    const toast = showStatusToast({
      status: "loading",
      titre: "Enregistrement en cours...",
      message: `Affectation de l'équipe pour l'année ${annee}.`,
    });

    try {
      const { data } = await httpClient.put(
        `https://backend-myalfa.vercel.app/api/clients/${userId}/equipe-annee/${annee}`,
        payload,
      );
      // Resynchronise avec la valeur exacte renvoyée par le serveur (id, etc.)
      setEquipeAnnees((prev) => {
        const others = prev.filter((e) => e.annee !== annee);
        return [...others, data].sort((a, b) => a.annee - b.annee);
      });
      patchSuiviBilanEquipeAnnee(userId, annee, data);
      toast.update({
        status: "success",
        titre: "Équipe assignée",
        message: `L'affectation pour l'année ${annee} est enregistrée.`,
      });
    } catch (error) {
      console.error("Erreur enregistrement équipe par année :", error);
      // Échec réel : on revient à l'état d'avant (formulaire + Suivi Bilan).
      setEquipeAnnees((prev) => {
        const others = prev.filter((e) => e.annee !== annee);
        return previousEntry
          ? [...others, previousEntry].sort((a, b) => a.annee - b.annee)
          : others;
      });
      patchSuiviBilanEquipeAnnee(userId, annee, previousEntry);
      toast.update({
        status: "error",
        titre: "Échec de l'enregistrement",
        message: "L'équipe par année n'a pas été enregistrée.",
      });
    }
  };

  const handleDeleteEquipeAnnee = async (annee) => {
    const confirmDelete = await dialog.confirm(
      `Supprimer la surcharge d'équipe pour l'année ${annee} ?`,
    );
    if (!confirmDelete) return;

    const previousEntry =
      equipeAnnees.find((e) => e.annee === annee) || null;

    // Retrait immédiat — formulaire ET Suivi Bilan.
    setEquipeAnnees((prev) => prev.filter((e) => e.annee !== annee));
    patchSuiviBilanEquipeAnnee(userId, annee, null);

    const toast = showStatusToast({
      status: "loading",
      titre: "Suppression en cours...",
      message: `Retrait de la surcharge pour l'année ${annee}.`,
    });

    try {
      await httpClient.delete(
        `https://backend-myalfa.vercel.app/api/clients/${userId}/equipe-annee/${annee}`,
      );
      toast.update({
        status: "success",
        titre: "Surcharge supprimée",
        message: `L'équipe de l'année ${annee} repasse au défaut du client.`,
      });
    } catch (error) {
      console.error("Erreur suppression équipe par année :", error);
      // Échec réel : on restaure la surcharge (formulaire + Suivi Bilan).
      setEquipeAnnees((prev) =>
        previousEntry
          ? [...prev, previousEntry].sort((a, b) => a.annee - b.annee)
          : prev,
      );
      patchSuiviBilanEquipeAnnee(userId, annee, previousEntry);
      toast.update({
        status: "error",
        titre: "Échec de la suppression",
        message: "La surcharge n'a pas pu être supprimée.",
      });
    }
  };

  const handleDateClotureChange = (e) => {
    const { name, value } = e.target;

    if (name === "date_cloture_jour") {
      setClient({
        ...client,
        dateClotureJour: value,
      });
    } else if (name === "date_cloture_mois") {
      setClient({
        ...client,
        dateClotureMois: value,
      });
    }
  };

  const handleMissionToggle = (item) => {
    setClient((prevClient) => ({
      ...prevClient,
      [item]: prevClient[item] === "oui" ? "non" : "oui",
    }));
  };

  // Les 5 rôles "par défaut" de l'équipe cabinet — si l'un d'eux change lors
  // de cette sauvegarde, on demande une année pour archiver l'ANCIENNE valeur
  // (pas la nouvelle) dans ClientEquipeAnnee, avant qu'elle ne soit écrasée.
  const DEFAULT_ROLE_FIELDS = [
    ["id_expertComptable", "email_expertComptable"],
    ["id_collaborateur", "email_collaborateur"],
    ["id_collaborateur_niv2", "email_collaborateur_niv2"],
    ["id_social", "email_social"],
    ["id_juridique", "email_juridique"],
  ];

  const handleSubmit = async () => {
    // Nouveau blocage : le motif est obligatoire (vérifié aussi côté backend).
    if (
      client.status === "Bloqué" &&
      !dejaBloque &&
      !String(client.statusDetails || "").trim()
    ) {
      dialog.alert("Merci de renseigner le motif du blocage.");
      return;
    }

    // Confirmation obligatoire avant tout renommage — seul canEditNom peut
    // atteindre ce point avec un nom différent, le champ étant en lecture
    // seule pour les autres (voir rendu du champ "Nom du dossier / société").
    if (canEditNom && client.nom !== selectedClient?.nom) {
      const confirmRename = await dialog.confirm(
        `Vous êtes sur le point de renommer ce dossier :\n\n"${selectedClient?.nom}" → "${client.nom}"\n\nConfirmer ce changement de nom ?`,
        { title: "Confirmation de renommage", variant: "danger" },
      );
      if (!confirmRename) return;
    }

    const fullDateCloture = `${client.dateClotureAnnee}-${client.dateClotureMois}-${client.dateClotureJour}`;
    const selectedCabinetId = client.cabinetId
      ? parseInt(client.cabinetId)
      : null;
    const clientToUpdate = {
      ...client,
      archiveDate: client.archiveDate
        ? new Date(client.archiveDate).toISOString()
        : null,
      dateCloture: fullDateCloture,
      cabinetId: selectedCabinetId,
      // Resynchronise l'objet cabinet imbriqué avec le cabinetId qui vient d'être
      // choisi, sinon la mise à jour optimiste affiche encore l'ancien nom de
      // cabinet le temps que la réponse serveur arrive.
      cabinet: selectedCabinetId
        ? cabinets.find((c) => c.id === selectedCabinetId) || null
        : null,
    };

    // Détecte les rôles d'équipe par défaut qui viennent de changer.
    const changedRoles = DEFAULT_ROLE_FIELDS.filter(
      ([idKey]) =>
        String(selectedClient?.[idKey] || "") !==
        String(clientToUpdate?.[idKey] || ""),
    );

    // Snapshot AVANT toute mise à jour optimiste, pour pouvoir revenir en
    // arrière si l'API échoue.
    let equipeAnneeArchive = null;
    let previousEquipeAnneeEntry = null;

    if (changedRoles.length > 0) {
      const anneeInput = await dialog.prompt(
        "Vous modifiez l'équipe cabinet. Les valeurs actuelles (avant modification) doivent être conservées pour une année précise dans Suivi Bilan — laquelle ?",
        String(new Date().getFullYear()),
        { title: "Archiver l'équipe précédente" },
      );
      const annee = parseInt(anneeInput, 10);
      if (!anneeInput || !Number.isInteger(annee)) {
        dialog.alert(
          "Modification annulée : une année valide est nécessaire pour archiver l'équipe précédente avant de la remplacer.",
        );
        return;
      }

      previousEquipeAnneeEntry =
        equipeAnnees.find((e) => e.annee === annee) || null;

      equipeAnneeArchive = { ...previousEquipeAnneeEntry, annee };
      changedRoles.forEach(([idKey, emailKey]) => {
        equipeAnneeArchive[idKey] = selectedClient?.[idKey] || null;
        equipeAnneeArchive[emailKey] = selectedClient?.[emailKey] || null;
      });
    }

    // `client` (hydraté depuis GET /api/clients/:id) embarque aussi toutes les
    // relations en lecture seule du dossier (historiques, todos, documents
    // GED, suivis...). Les renvoyer telles quelles dans le PUT peut dépasser
    // la limite de taille de requête du serveur pour un dossier avec beaucoup
    // d'historique (PayloadTooLargeError) — le backend ne lit de toute façon
    // que ces champs scalaires (bdClientController.js#updateClient), donc
    // seul ce sous-ensemble part réellement à l'API.
    const EDITABLE_CLIENT_FIELDS = [
      "civilite",
      "nom",
      "nomClient",
      "prenomClient",
      "email",
      "mdp",
      "telephone",
      "fonctionContact",
      "civilite2",
      "nomClient2",
      "prenomClient2",
      "fonctionContact2",
      "email2",
      "telephone2",
      "formeSociale",
      "dateCloture",
      "dateDebutMission",
      "regimeFiscal",
      "regimeTVA",
      "taxeSalaires",
      "expertComptable",
      "superviseur",
      "collaborateur",
      "collaborateur_niv2",
      "missionComptable",
      "missionSociale",
      "missionJuridique",
      "missionIR",
      "codesMissionComptable",
      "codesMissionSociale",
      "codesMissionJuridique",
      "ponderation",
      "activite",
      "plateformeElectronique",
      "accesEC",
      "holding",
      "personnePhysique",
      "email_expertComptable",
      "id_expertComptable",
      "email_superviseur",
      "id_superviseur",
      "email_collaborateur",
      "id_collaborateur",
      "email_collaborateur_niv2",
      "id_collaborateur_niv2",
      "email_social",
      "id_social",
      "email_juridique",
      "id_juridique",
      "siren",
      "siret",
      "adresse",
      "cabinetId",
      "status",
      "archiveDate",
      "statusDetails",
      "vigilance",
      "risqueLocalisation",
      "paysJuridiction",
      "activiteHorsUE",
      "presenceGAFI",
      "risqueMission",
      "typeMission",
      "volumeFluxAnnuels",
      "encaissementsEspeces",
      "risqueActivite",
      "codeNAF",
      "secteurActivite",
      "risqueClient",
      "typeClient",
      "relationPolitique",
      "profilReactivite",
      "profilConformite",
      "profilRentabilite",
      "profilComplexite",
      "profilAvance",
      "profilAutonomie",
      "radarLocalisation",
      "radarMission",
      "radarActivite",
      "radarClient",
      "observations",
    ];
    const payloadForApi = {};
    EDITABLE_CLIENT_FIELDS.forEach((key) => {
      payloadForApi[key] = clientToUpdate[key];
    });

    // Dossier déjà actif dont les missions (ou leurs codes) changent : la
    // fenêtre « Notifier les collaborateurs ? » s'ouvre par-dessus ce
    // formulaire ; « Non » comme « Oui » ferment les deux et enregistrent ;
    // le mail part une fois l'enregistrement confirmé (voir plus bas).
    const notifierEquipe = await demanderNotificationMissions({
      clientNom: clientToUpdate.nom || selectedClient?.nom,
      avant: selectedClient,
      apres: clientToUpdate,
      users: allUsers,
      missions: kycReferentiel?.listes?.missions || [],
    });

    // Mise à jour optimiste + fermeture immédiate du modal ; l'appel API se
    // poursuit en arrière-plan (voir catch : revert + alerte si ça échoue).
    onUserUpdated(clientToUpdate);
    // Idem pour la ligne du client dans Suivi Bilan (tableau + récap par
    // collaborateur), Suivi Juridique AGO, Juridique Ponctuelle, Suivi CFE
    // et Suivi Compta : patch instantané, sans attendre la réponse serveur.
    patchSuiviBilanClient(userId, clientToUpdate);
    patchSuiviJuridiqueClient(userId, clientToUpdate);
    patchJuridiquePonctuelleClient(userId, clientToUpdate);
    patchSuiviCFEClient(userId, clientToUpdate);
    patchSuiviComptaClient(userId, clientToUpdate);
    if (equipeAnneeArchive) {
      setEquipeAnnees((prev) => {
        const others = prev.filter((e) => e.annee !== equipeAnneeArchive.annee);
        return [...others, equipeAnneeArchive].sort((a, b) => a.annee - b.annee);
      });
      patchSuiviBilanEquipeAnnee(
        userId,
        equipeAnneeArchive.annee,
        equipeAnneeArchive,
      );
    }
    onCancel();

    const toast = showStatusToast({
      status: "loading",
      titre: "Mise à jour en cours...",
      message: equipeAnneeArchive
        ? `Enregistrement des modifications et archivage de l'équipe ${equipeAnneeArchive.annee}.`
        : "Enregistrement des modifications dans toute la plateforme.",
    });

    // `onCancel()` démonte ce composant juste au-dessus : les callbacks
    // onSuccess/onError passés à mutate() sont liés au cycle de vie du
    // composant appelant et peuvent ne jamais se déclencher une fois démonté.
    // mutateAsync() renvoie une vraie Promise, indépendante du montage — on
    // l'enchaîne directement pour que le toast se mette à jour de façon fiable.
    // (La mutation elle-même — useUpdateClient — recharge le client complet en
    // tâche de fond et synchronise le cache React Query partagé ['clients'] +
    // ['client', id], donc tous les écrans qui le lisent se mettent à jour.)
    // Les deux requêtes (client + éventuel archivage équipe par année) sont
    // combinées : le toast ne passe au succès que si tout est réellement
    // enregistré côté serveur/base — sinon tout est annulé (revert complet).
    Promise.all([
      updateClientMutation.mutateAsync({ id: userId, payload: payloadForApi }),
      equipeAnneeArchive
        ? httpClient.put(
            `https://backend-myalfa.vercel.app/api/clients/${userId}/equipe-annee/${equipeAnneeArchive.annee}`,
            equipeAnneeArchive,
          )
        : Promise.resolve(null),
    ])
      .then(([, equipeAnneeResponse]) => {
        toast.update({
          status: "success",
          titre: "Mise à jour appliquée",
          message: "Les modifications sont appliquées dans tout le système.",
        });
        // « Oui, notifier » : mail à l'équipe une fois l'enregistrement confirmé.
        if (notifierEquipe) envoyerNotificationMissions({ clientId: userId, avant: selectedClient });
        // Resynchronise avec la valeur exacte renvoyée par le serveur (id, etc.)
        if (equipeAnneeArchive && equipeAnneeResponse?.data) {
          setEquipeAnnees((prev) => {
            const others = prev.filter(
              (e) => e.annee !== equipeAnneeArchive.annee,
            );
            return [...others, equipeAnneeResponse.data].sort(
              (a, b) => a.annee - b.annee,
            );
          });
          patchSuiviBilanEquipeAnnee(
            userId,
            equipeAnneeArchive.annee,
            equipeAnneeResponse.data,
          );
        }
        // Les 5 écrans Suivi Prod (Bilan, Juridique AGO, Juridique
        // Ponctuelle, CFE, Compta) ont déjà été patchés de façon optimiste
        // ci-dessus (voir patchSuiviBilanClient / patchSuiviJuridiqueClient /
        // patchJuridiquePonctuelleClient / patchSuiviCFEClient /
        // patchSuiviComptaClient) ; ces fetch re-synchronisent chacun avec le
        // serveur en silence (pas de skeleton tant que des données sont déjà
        // en mémoire).
        fetchSuiviBilan();
        fetchSuiviJuridique();
        fetchJuridiquePonctuelle();
        fetchSuiviCFE();
        fetchSuiviCompta();
        // Suivi Social n'a PAS d'équivalent patchSuiviSocialClient : contrairement
        // aux autres écrans Suivi, /api/suiviSocial filtre côté serveur sur
        // missionSociale = "oui" (voir suiviSocialController.js) — un simple
        // patch sur les lignes déjà chargées ne peut pas faire apparaître/
        // disparaître un client dont on vient de (dé)cocher la mission Sociale,
        // il faut un vrai refetch pour refléter ça sans recharger toute la page.
        fetchSuiviSocial();
      })
      .catch((error) => {
        console.error("Erreur mise à jour client :", error);
        // Échec réel de l'enregistrement : le modal est déjà fermé, donc sans
        // ça l'échec est invisible pour l'utilisateur. On revient à l'état
        // serveur d'origine et on prévient.
        onUserUpdated(selectedClient);
        patchSuiviBilanClient(userId, selectedClient);
        patchSuiviJuridiqueClient(userId, selectedClient);
        patchJuridiquePonctuelleClient(userId, selectedClient);
        patchSuiviCFEClient(userId, selectedClient);
        patchSuiviComptaClient(userId, selectedClient);
        if (equipeAnneeArchive) {
          setEquipeAnnees((prev) => {
            const others = prev.filter(
              (e) => e.annee !== equipeAnneeArchive.annee,
            );
            return previousEquipeAnneeEntry
              ? [...others, previousEquipeAnneeEntry].sort(
                  (a, b) => a.annee - b.annee,
                )
              : others;
          });
          patchSuiviBilanEquipeAnnee(
            userId,
            equipeAnneeArchive.annee,
            previousEquipeAnneeEntry,
          );
        }
        toast.update({
          status: "error",
          titre: "Échec de la mise à jour",
          message: equipeAnneeArchive
            ? "La mise à jour et l'archivage de l'équipe n'ont pas été appliqués."
            : "La mise à jour n'est pas appliquée dans le système.",
        });
      });
  };

  if (isFetchingClient) {
    return (
      <div
        className="CreateContainer"
        style={{ textAlign: "center", padding: "2rem" }}
      >
        <div className="spinner" />
        <br />
      </div>
    );
  }

  return (
    <>
    {showAccesSecret && dejaSecret && selectedClient && (
      <FenetreAccesSecret
        client={selectedClient}
        etat={dossierSecret.etat}
        users={allUsers}
        actions={dossierSecret}
        onClose={() => setShowAccesSecret(false)}
      />
    )}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "90%",
      }}
    >
      <div className="flex justify-between mb-4 items-center">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <div>
            <div className="text-1xl font-bold text-slate-900">
              {client.nom}
            </div>
            <div className="flex items-center gap-2 mt-1">
              {canEditStatus ? (
                <select
                  name="status"
                  value={client.status || ""}
                  onChange={(e) => (e.target.value === CHOIX_SECRET ? ouvrirSecret() : handleStatusChange(e))}
                  disabled={dejaBloque && !peutBloquer}
                  title={
                    dejaBloque && !peutBloquer
                      ? "Seuls les administrateurs, les experts-comptables et le secrétariat peuvent débloquer ce dossier."
                      : undefined
                  }
                  className="border border-slate-300 rounded-md
            h-5 px-2 text-xs leading-tight w-full
            bg-white disabled:opacity-60"
                >
                  {["Actif", "Bloqué", "Archivé"]
                    .filter(
                      (item) =>
                        item !== "Bloqué" || peutBloquer || client.status === "Bloqué",
                    )
                    .map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                  {choixSecret && (
                    <option value={CHOIX_SECRET}>{dejaSecret ? "Secret (gérer les accès)" : "Secret"}</option>
                  )}
                </select>
              ) : (
                <span className="text-xs text-slate-500">{client.status || "Actif"}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-4  mr-10">
          <button
            onClick={onCancel}
            variant="outline"
            className={`flex items-center gap-2 h-8 px-3 rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50`}
          >
            Annuler
          </button>
          <div
            className="cursor-pointer flex items-center gap-2 h-8 px-3 rounded-md font-semibold text-sm text-white border bg-green-600 hover:bg-green-700 border-green-700 transition-colors"
            onClick={() => handleSubmit()}
          >
            <Save className="w-4 h-4 mr-2" />
            Enregistrer
          </div>
        </div>
      </div>
      {canEditStatus && (client.status === "Archivé" || client.status === "Bloqué") && (
        <div className="mt-0 mb-4 px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 flex items-center gap-4">
          {/* 📝 Détail archivage / motif du blocage */}
          <input
            type="text"
            name="statusDetails"
            placeholder={
              client.status === "Bloqué"
                ? "motif du blocage (obligatoire)..."
                : "motif de la fin de mission..."
            }
            value={client.statusDetails || ""}
            onChange={handleChange}
            disabled={client.status === "Bloqué" && dejaBloque}
            className="flex-1 h-9 px-3 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:bg-gray-100"
          />

          {/* 📅 Date archivage */}
          {client.status === "Archivé" && (
            <input
              type="date"
              name="archiveDate"
              value={client.archiveDate ? client.archiveDate.split("T")[0] : ""}
              onChange={handleChange}
              className="h-9 px-3 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
            />
          )}
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#9B1B5D]" />
              Information client
            </h3>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Nom du dossier / société</span>
                {canEditNom ? (
                  <input
                    className="border border-slate-300 rounded-md
             h-5 px-2 text-xs leading-tight w-1/2"
                    type="text"
                    name="nom"
                    placeholder="nom du dossier"
                    value={client.nom}
                    onChange={handleChange}
                    required
                  />
                ) : (
                  <span
                    className="text-xs text-slate-700 w-1/2 text-right"
                    title="Seul un administrateur peut renommer ce dossier — contactez-le si besoin."
                  >
                    {client.nom}
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">SIREN</span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="siren"
                  placeholder="siren"
                  value={client.siren}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">SIRET</span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="siret"
                  placeholder="siret"
                  value={client.siret}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Forme sociale</span>
                <FormeSocialeSelect
                  formes={kycReferentiel?.listes?.formes}
                  value={client.formeSociale[0] || ""}
                  onChange={(v) => handleChange({ target: { name: "formeSociale", value: v } })}
                  className="border border-slate-300 rounded-md 
                  h-5 px-2 text-xs leading-tight w-1/2
                  bg-white"
                />
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Date de clôture</span>
                <div className="flex gap-1">
                  <select
                    name="date_cloture_jour"
                    value={client.dateClotureJour || ""}
                    onChange={handleDateClotureChange}
                    className="border border-slate-300 rounded-md 
                    h-5 px-2 text-xs leading-tight 
                    w-12 bg-white"
                  >
                    {[...Array(31).keys()].map((day) => (
                      <option
                        key={day}
                        value={String(day + 1).padStart(2, "0")}
                      >
                        {String(day + 1).padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                  <select
                    name="date_cloture_mois"
                    value={client.dateClotureMois || ""}
                    onChange={handleDateClotureChange}
                    className="border border-slate-300 rounded-md 
                    h-5 px-2 text-xs leading-tight 
                    w-25 bg-white"
                  >
                    {[
                      "Janvier",
                      "Février",
                      "Mars",
                      "Avril",
                      "Mai",
                      "Juin",
                      "Juillet",
                      "Août",
                      "Septembre",
                      "Octobre",
                      "Novembre",
                      "Décembre",
                    ].map((month, index) => (
                      <option
                        key={index}
                        value={String(index + 1).padStart(2, "0")}
                      >
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span
                  className="text-slate-500"
                  title="Le dossier apparaît dans les tableaux SuiviProd à partir de l'année de cette date. Vide = visible sur toutes les années."
                >
                  Date de début de mission
                </span>
                <input
                  className="border border-slate-300 rounded-md
             h-5 px-2 text-xs leading-tight w-1/2 bg-white"
                  type="date"
                  name="dateDebutMission"
                  value={
                    client.dateDebutMission
                      ? String(client.dateDebutMission).split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Régime fiscal</span>
                <select
                  name="regimeFiscal"
                  value={client.regimeFiscal || ""}
                  onChange={handleChange}
                  className="border border-slate-300 rounded-md 
            h-5 px-2 text-xs leading-tight w-1/2
            bg-white"
                >
                  <option value=""> Régime Fiscal </option>
                  {[
                    "IS RS",
                    "IS RN",
                    "IR BIC Micro",
                    "IR BIC RS",
                    "IR BIC RN",
                    "IR BNC micro",
                    "IR BNC réel",
                    "IR RF",
                    "IR LMNP",
                  ].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">TVA</span>
                <select
                  className="border border-slate-300 rounded-md 
                  h-5 px-2 text-xs leading-tight w-1/2
                  bg-white"
                  name="regimeTVA"
                  value={client.regimeTVA || ""}
                  onChange={handleChange}
                >
                  <option value=""> Régime TVA </option>
                  {["N/A", "Franchise", "RSI", "RN", "Mini réel"].map(
                    (item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Adresse</span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="adresse"
                  placeholder="adresse"
                  value={client.adresse}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Activité</span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="activite"
                  placeholder="activite"
                  value={client.activite}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            {/* Code activité NAF et pays : choisis UNIQUEMENT dans le
                référentiel des Paramètres KYC/LAB (déjà chargé dans
                EtatGlobal) — utilisés par l'évaluation « Risques & vigilance »
                du module KYC (axes Activité et Localisation). */}
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center gap-2">
                <span className="text-slate-500">Code activité (NAF)</span>
                <ReferentielSelect
                  entries={kycReferentiel?.listes?.activites || []}
                  value={client.codeNAF || ""}
                  onChange={(e) =>
                    setClient((prev) => ({
                      ...prev,
                      codeNAF: e ? e.code : null,
                      // L'activité reste un champ libre : proposée depuis le
                      // libellé NAF, modifiable ensuite.
                      ...(e && { activite: e.libelle }),
                    }))
                  }
                  placeholder={kycReferentiel ? "Code ou libellé NAF…" : "Chargement du référentiel…"}
                  disabled={!kycReferentiel}
                  className="w-1/2"
                  inputClassName="border border-slate-300 rounded-md h-5 px-2 text-xs leading-tight bg-white"
                />
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center gap-2">
                <span className="text-slate-500">Pays (siège)</span>
                <ReferentielSelect
                  entries={kycReferentiel?.listes?.pays || []}
                  value={client.paysJuridiction || ""}
                  onChange={(e) => setClient((prev) => ({ ...prev, paysJuridiction: e ? e.code : null }))}
                  placeholder={kycReferentiel ? "Code ou nom du pays…" : "Chargement du référentiel…"}
                  disabled={!kycReferentiel}
                  className="w-1/2"
                  inputClassName="border border-slate-300 rounded-md h-5 px-2 text-xs leading-tight bg-white"
                />
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Solution de gestion / PA</span>
                <select
                  name="plateformeElectronique"
                  value={client.plateformeElectronique || ""}
                  onChange={handlePlateformeElectroniqueChange}
                  className="border border-slate-300 rounded-md
                  h-5 px-2 text-xs leading-tight w-1/2
                  bg-white"
                >
                  <option value="">-- Aucune --</option>
                  {PLATEFORME_ELECTRONIQUE_OPTIONS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Accès EC</span>
                <select
                  name="accesEC"
                  value={client.accesEC || "non"}
                  onChange={handleChange}
                  className="border border-slate-300 rounded-md
                  h-5 px-2 text-xs leading-tight w-1/2
                  bg-white"
                >
                  <option value="non">Non</option>
                  <option value="oui">Oui</option>
                </select>
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Ponderation</span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="ponderation"
                  placeholder="ponderation"
                  value={client.ponderation}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="space-y-2 mb-2">
              <div className="flex justify-between text-xs items-center">
                <span className="text-slate-500">Cabinet ALFA</span>
                <select
                  name="cabinetId"
                  value={client.cabinetId ?? ""}
                  onChange={handleChange}
                  className="border border-slate-300 rounded-md
            h-5 px-2 text-xs leading-tight w-1/2
            bg-white"
                >
                  <option value="">-- Aucun --</option>
                  {cabinets.map((cab) => (
                    <option key={cab.id} value={cab.id}>
                      {cab.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
              <p className="text-xs font-semibold text-slate-700 mb-1">
                Contact représentant
              </p>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Civilité </span>
                <select
                  name="civilite"
                  value={client.civilite || ""}
                  onChange={handleChange}
                  className="border border-slate-300 rounded-md 
            h-5 px-2 text-xs leading-tight w-1/2
            bg-white"
                >
                  <option value="">-- Civilité --</option>
                  {["Monsieur", "Madame"].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Nom </span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="nomClient"
                  placeholder="nomClient"
                  value={client.nomClient}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Prénom</span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="prenomClient"
                  placeholder="prenomClient"
                  value={client.prenomClient}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Fonction</span>
                <input
                  className="border border-slate-300 rounded-md
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="fonctionContact"
                  placeholder="Ex. Gérant"
                  value={client.fonctionContact}
                  onChange={handleChange}
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Email</span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="email"
                  placeholder="email"
                  value={client.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Téléphone</span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="telephone"
                  placeholder="telephone"
                  value={client.telephone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Mdp MyAlfa Link</span>
                <input
                  className="border border-slate-300 rounded-md 
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="password"
                  name="mdp"
                  placeholder="****"
                  value={client.mdp}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2 mt-2 space-y-1.5">
              <p className="text-xs font-semibold text-slate-700 mb-1">
                Contact représentant 2 (optionnel)
              </p>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Civilité </span>
                <select
                  name="civilite2"
                  value={client.civilite2 || ""}
                  onChange={handleChange}
                  className="border border-slate-300 rounded-md
            h-5 px-2 text-xs leading-tight w-1/2
            bg-white"
                >
                  <option value="">-- Civilité --</option>
                  {["Monsieur", "Madame"].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Nom </span>
                <input
                  className="border border-slate-300 rounded-md
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="nomClient2"
                  placeholder="nomClient2"
                  value={client.nomClient2 || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Prénom</span>
                <input
                  className="border border-slate-300 rounded-md
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="prenomClient2"
                  placeholder="prenomClient2"
                  value={client.prenomClient2 || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Fonction</span>
                <input
                  className="border border-slate-300 rounded-md
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="fonctionContact2"
                  placeholder="Ex. Gérant"
                  value={client.fonctionContact2 || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Email</span>
                <input
                  className="border border-slate-300 rounded-md
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="email2"
                  placeholder="email2"
                  value={client.email2 || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Téléphone</span>
                <input
                  className="border border-slate-300 rounded-md
             h-5 px-2 text-xs leading-tight w-1/2"
                  type="text"
                  name="telephone2"
                  placeholder="telephone2"
                  value={client.telephone2 || ""}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#9B1B5D]" />
              Équipe Cabinet
            </h3>
            <div className="space-y-2.5">
              {/* Expert comptable */}
              <div className="flex items-center gap-2 p-2 bg-[#9B1B5D]/5 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-[#9B1B5D] flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500">Expert comptable</p>
                  <select
                    className="border border-slate-300 rounded-md 
                                    h-5 px-2 text-xs leading-tight w-full
                                    bg-white"
                    name="expertComptable"
                    value={client.expertComptable}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const selectedExpert = experts.find(
                        (expert) =>
                          `${expert.name} ${expert.lastName}` === selectedName,
                      );

                      setClient((prev) => ({
                        ...prev,
                        expertComptable: selectedName,
                        email_expertComptable: selectedExpert?.email || "",
                        id_expertComptable: selectedExpert?.id || "",
                      }));
                    }}
                  >
                    <option value="">-- Aucun --</option>
                    {experts.map((expert) => (
                      <option
                        key={expert.id}
                        value={`${expert.name} ${expert.lastName}`}
                      >
                        {expert.name} {expert.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Collaborateurs */}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Collaborateur Niv. 1</p>
                  <select
                    className="border border-slate-300 rounded-md 
                    h-5 px-2 text-xs leading-tight w-full
                    bg-white"
                    name="collaborateur"
                    value={client.collaborateur || ""}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const selectedUser = collaborateurs.find(
                        (user) =>
                          `${user.name} ${user.lastName}` === selectedName,
                      );

                      setClient((prev) => ({
                        ...prev,
                        collaborateur: selectedName,
                        email_collaborateur: selectedUser?.email || "",
                        id_collaborateur: selectedUser?.id || "",
                      }));
                    }}
                  >
                    <option value="">-- Aucun --</option>
                    {collaborateurs.map((user) => (
                      <option
                        key={user.id}
                        value={`${user.name} ${user.lastName}`}
                      >
                        {user.name} {user.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Collaborateur Niv. 2</p>
                  <select
                    className="border border-slate-300 rounded-md 
                    h-5 px-2 text-xs leading-tight w-full
                    bg-white"
                    name="collaborateur_niv2"
                    value={client.collaborateur_niv2}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const selectedUser = collaborateurs_niv2.find(
                        (user) =>
                          `${user.name} ${user.lastName}` === selectedName,
                      );

                      setClient((prev) => ({
                        ...prev,
                        collaborateur_niv2: selectedName,
                        email_collaborateur_niv2: selectedUser?.email || "",
                        id_collaborateur_niv2: selectedUser?.id || "",
                      }));
                    }}
                  >
                    <option value="">-- Aucun --</option>
                    {collaborateurs_niv2.map((user) => (
                      <option
                        key={user.id}
                        value={`${user.name} ${user.lastName}`}
                      >
                        {user.name} {user.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Collaborateur Social</p>
                  <select
                    name="social"
                    className="border border-slate-300 rounded-md 
                h-5 px-2 text-xs leading-tight w-full
                bg-white"
                    value={client.social}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const selectedUser = socials.find(
                        (user) =>
                          `${user.name} ${user.lastName}` === selectedName,
                      );

                      setClient((prev) => ({
                        ...prev,
                        social: selectedName,
                        email_social: selectedUser?.email || "",
                        id_social: selectedUser?.id || "",
                      }));
                    }}
                  >
                    <option value="">-- Aucun --</option>
                    {socials.map((user) => (
                      <option
                        key={user.id}
                        value={`${user.name} ${user.lastName}`}
                      >
                        {user.name} {user.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">
                    Collaborateur Juridique
                  </p>
                  <select
                    name="juridique"
                    className="border border-slate-300 rounded-md 
                h-5 px-2 text-xs leading-tight w-full
                bg-white"
                    value={client.juridique}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const selectedUser = juridiques.find(
                        (user) =>
                          `${user.name} ${user.lastName}` === selectedName,
                      );

                      setClient((prev) => ({
                        ...prev,
                        juridique: selectedName,
                        email_juridique: selectedUser?.email || "",
                        id_juridique: selectedUser?.id || "",
                      }));
                    }}
                  >
                    <option value="">-- Aucun --</option>
                    {juridiques.map((user) => (
                      <option
                        key={user.id}
                        value={`${user.name} ${user.lastName}`}
                      >
                        {user.name} {user.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Assigner par année : surcharge indépendante de l'équipe par
                  défaut ci-dessus, pour une année précise (voir
                  FicheClientOverlay.js pour la liste en lecture seule). */}
              <div className="pt-2 mt-1 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700">
                    Équipe par année
                  </p>
                  <button
                    type="button"
                    onClick={openAddAnnee}
                    className="text-xs text-[#9B1B5D] font-medium hover:underline"
                  >
                    + Assigner par année
                  </button>
                </div>

                {equipeAnnees.length === 0 && !showAddAnnee && (
                  <p className="text-xs text-slate-400 italic">
                    Aucune surcharge par année.
                  </p>
                )}

                <div className="space-y-1.5">
                  {equipeAnnees.map((eq) => (
                    <div
                      key={eq.annee}
                      className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5"
                    >
                      <button
                        type="button"
                        onClick={() => openEditAnnee(eq)}
                        className="text-xs text-slate-700 text-left flex-1"
                      >
                        <span className="font-semibold text-[#9B1B5D]">
                          {eq.annee}
                        </span>{" "}
                        — modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteEquipeAnnee(eq.annee)}
                        className="text-slate-400 hover:text-red-600 ml-2 flex-shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                {showAddAnnee && (
                  <div className="mt-2 p-2.5 border border-slate-200 rounded-md bg-slate-50 space-y-2">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Année</p>
                      <input
                        type="number"
                        value={draftAnnee.annee}
                        onChange={(e) =>
                          setDraftAnnee((prev) => ({
                            ...prev,
                            annee: e.target.value,
                          }))
                        }
                        placeholder="ex: 2026"
                        className="border border-slate-300 rounded-md h-7 px-2 text-xs w-full bg-white"
                      />
                    </div>

                    <div>
                      <p className="text-xs text-slate-500 mb-1">
                        Expert comptable
                      </p>
                      <select
                        className="border border-slate-300 rounded-md h-7 px-2 text-xs w-full bg-white"
                        value={draftAnnee.expertComptable}
                        onChange={(e) => {
                          const selectedName = e.target.value;
                          const selectedUser = experts.find(
                            (u) => `${u.name} ${u.lastName}` === selectedName,
                          );
                          setDraftAnnee((prev) => ({
                            ...prev,
                            expertComptable: selectedName,
                            email_expertComptable: selectedUser?.email || "",
                            id_expertComptable: selectedUser?.id || "",
                          }));
                        }}
                      >
                        <option value="">-- Aucun --</option>
                        {experts.map((u) => (
                          <option key={u.id} value={`${u.name} ${u.lastName}`}>
                            {u.name} {u.lastName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">
                          Collaborateur Niv. 1
                        </p>
                        <select
                          className="border border-slate-300 rounded-md h-7 px-2 text-xs w-full bg-white"
                          value={draftAnnee.collaborateur}
                          onChange={(e) => {
                            const selectedName = e.target.value;
                            const selectedUser = collaborateurs.find(
                              (u) => `${u.name} ${u.lastName}` === selectedName,
                            );
                            setDraftAnnee((prev) => ({
                              ...prev,
                              collaborateur: selectedName,
                              email_collaborateur: selectedUser?.email || "",
                              id_collaborateur: selectedUser?.id || "",
                            }));
                          }}
                        >
                          <option value="">-- Aucun --</option>
                          {collaborateurs.map((u) => (
                            <option
                              key={u.id}
                              value={`${u.name} ${u.lastName}`}
                            >
                              {u.name} {u.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">
                          Collaborateur Niv. 2
                        </p>
                        <select
                          className="border border-slate-300 rounded-md h-7 px-2 text-xs w-full bg-white"
                          value={draftAnnee.collaborateur_niv2}
                          onChange={(e) => {
                            const selectedName = e.target.value;
                            const selectedUser = collaborateurs_niv2.find(
                              (u) => `${u.name} ${u.lastName}` === selectedName,
                            );
                            setDraftAnnee((prev) => ({
                              ...prev,
                              collaborateur_niv2: selectedName,
                              email_collaborateur_niv2:
                                selectedUser?.email || "",
                              id_collaborateur_niv2: selectedUser?.id || "",
                            }));
                          }}
                        >
                          <option value="">-- Aucun --</option>
                          {collaborateurs_niv2.map((u) => (
                            <option
                              key={u.id}
                              value={`${u.name} ${u.lastName}`}
                            >
                              {u.name} {u.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Social</p>
                        <select
                          className="border border-slate-300 rounded-md h-7 px-2 text-xs w-full bg-white"
                          value={draftAnnee.social}
                          onChange={(e) => {
                            const selectedName = e.target.value;
                            const selectedUser = socials.find(
                              (u) => `${u.name} ${u.lastName}` === selectedName,
                            );
                            setDraftAnnee((prev) => ({
                              ...prev,
                              social: selectedName,
                              email_social: selectedUser?.email || "",
                              id_social: selectedUser?.id || "",
                            }));
                          }}
                        >
                          <option value="">-- Aucun --</option>
                          {socials.map((u) => (
                            <option
                              key={u.id}
                              value={`${u.name} ${u.lastName}`}
                            >
                              {u.name} {u.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Juridique</p>
                        <select
                          className="border border-slate-300 rounded-md h-7 px-2 text-xs w-full bg-white"
                          value={draftAnnee.juridique}
                          onChange={(e) => {
                            const selectedName = e.target.value;
                            const selectedUser = juridiques.find(
                              (u) => `${u.name} ${u.lastName}` === selectedName,
                            );
                            setDraftAnnee((prev) => ({
                              ...prev,
                              juridique: selectedName,
                              email_juridique: selectedUser?.email || "",
                              id_juridique: selectedUser?.id || "",
                            }));
                          }}
                        >
                          <option value="">-- Aucun --</option>
                          {juridiques.map((u) => (
                            <option
                              key={u.id}
                              value={`${u.name} ${u.lastName}`}
                            >
                              {u.name} {u.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setShowAddAnnee(false)}
                        className="px-3 h-7 rounded-md text-xs font-medium border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveEquipeAnnee}
                        className="px-3 h-7 rounded-md text-xs font-semibold bg-[#840040] hover:bg-[#6e1a36] text-white"
                      >
                        Enregistrer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <br />

        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#9B1B5D]" />
            Missions récurrentes{" "}
            <span className="text-xs font-light mt-0.5">
              {" "}
              ( sélectionner pour modifier )
            </span>
          </h3>

          {/* Cases des missions + codes de la liste « Missions » des
              Paramètres KYC/LAB (plusieurs par mission). */}
          <MissionsRecurrentes
            client={client}
            missions={kycReferentiel?.listes?.missions || []}
            onToggle={handleMissionToggle}
            onCodes={(champ, codes) => setClient((prev) => ({ ...prev, [champ]: codes }))}
            peutCoder={ROLES_CODES_MISSION.includes(utilisateur?.role)}
          />
        </div>
        <br />

        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#9B1B5D]" />
            Groupement
          </h3>
          <p className="text-[11px] text-slate-400 mb-2">
            Contacts de l'Annuaire liés à ce dossier — mêmes liens que les
            "dossiers liés" de l'Annuaire. Les clients partageant un même
            contact apparaissent groupés dans le Portefeuille.
          </p>

          {linkedAnnuaires.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {linkedAnnuaires.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-1.5 h-6 px-2 rounded-md border border-slate-300 bg-slate-50 text-xs"
                >
                  <span className="text-slate-800 font-medium">
                    {a.nom} {a.prenoms}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDetachGroupementContact(a)}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div
            className="flex items-center gap-3 text-xs relative"
            ref={groupementBoxRef}
          >
            <div className="relative w-4/5">
              <input
                type="text"
                value={groupementSearch}
                onChange={(e) => {
                  setGroupementSearch(e.target.value);
                  setShowGroupementDropdown(true);
                }}
                onFocus={() => setShowGroupementDropdown(true)}
                placeholder="Rechercher un contact (nom, prénom)..."
                className="border border-slate-300 rounded-md
                   h-5 px-2 text-xs leading-tight
                   w-full bg-white"
              />
              {showGroupementDropdown && groupementSearch.trim() && (
                <div className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg">
                  {groupementSuggestions.length === 0 ? (
                    <p className="px-2 py-1.5 text-slate-400">
                      Aucun contact trouvé
                    </p>
                  ) : (
                    groupementSuggestions.map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => handleAttachGroupementContact(c)}
                        className="w-full text-left px-2 py-1.5 hover:bg-slate-50 text-slate-700"
                      >
                        {c.nom} {c.prenoms}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowCreateGroupementContact(true)}
              className="h-5 px-3 rounded-md text-xs font-semibold
                   bg-[#9B1B5D] text-white
                   hover:bg-[#7A1548] transition w-1/5"
            >
              + Créer
            </button>
          </div>
        </div>

        <br />
        {/* <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr",
            alignItems: "center",
            gap: "5%",
          }}
        >
          <label>Holding </label>
          <input
            type="checkbox"
            checked={client["holding"] === true}
            onChange={() => handleMissionToggle("holding")}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr",
            alignItems: "center",
            gap: "5%",
            marginTop: "1%",
          }}
        >
          <label>
            Si oui, l'associé unique est personne physique est dirigeant ?{" "}
          </label>
          <input
            type="checkbox"
            checked={client["personnePhysique"] === true}
            onChange={() => handleMissionToggle("personnePhysique")}
          />
        </div> */}

        <br />
        <br />
      </form>
    </div>
    {showCreateGroupementContact && (
      <FormOverlay onClose={() => setShowCreateGroupementContact(false)}>
        <NouveauContactForm
          onContactCreated={handleGroupementContactCreated}
          onBack={() => setShowCreateGroupementContact(false)}
          onCancel={() => setShowCreateGroupementContact(false)}
        />
      </FormOverlay>
    )}
    </>
  );
}

export default Update;
